import { SupportedLanguage } from '@common'
import { AnyAction, ListenerEffectAPI, isAnyOf } from '@reduxjs/toolkit'
import router from 'next/router'

import { getDraftQuerySelector, api, updateDraftSelector } from '../api'
import {
  addProduct,
  reCreateCart,
  removeProduct,
  setBillingCountry,
  setCreatorBoostCode,
  syncWithDraft,
  changeLanguage,
} from '../features/checkout/checkoutSlice'
import { addNotification } from '../features/notifications/notificationSlice'
import { LOGIN_STATUS } from '../features/user/userSlice'
import { AppDispatch, RootState } from '../store'
import { compareCartRows } from '../utils/compareCartRows'
import logger from '../utils/logger'
import { normalizeCreatorBoostCode } from '../utils/normalizeCreatorBoostCode'
import { throwIfAborted } from './throwIfAborted'

const updateDraft = async (action: AnyAction, listenerAPI: ListenerEffectAPI<RootState, AppDispatch>) => {
  logger.debug({ label: 'updateDraftMiddleware', msg: 'action', action })
  if (listenerAPI.getState().user.loginStatus !== LOGIN_STATUS.LOGGED_IN) {
    logger.debug({ label: 'updateDraftMiddleware', msg: 'user not logged in ' })
    return
  }
  listenerAPI.cancelActiveListeners()

  const checkoutState = listenerAPI.getState().checkout
  const configState = listenerAPI.getState().config
  const creatorBoostCode = checkoutState.creatorBoostCode

  const gameId = listenerAPI.getState().common.currentGame
  if (!gameId) {
    logger.debug({ label: 'updateDraftMiddleware', msg: 'gameId is null' })
    return
  }

  await listenerAPI.condition((_, state) => {
    const data = getDraftQuerySelector(gameId)(state)
    return data.isSuccess
  })

  if (updateDraftSelector(listenerAPI.getState()).isLoading) {
    try {
      await listenerAPI.condition((_, state) => {
        logger.debug({
          label: 'updateDraftMiddleware',
          msg: 'condition',
          isLoading: updateDraftSelector(state).isLoading,
        })

        const res = !updateDraftSelector(state).isLoading
        return res
      })
      logger.debug({ label: 'updateDraftMiddleware', msg: 'resolves' })
    } catch (err) {
      logger.debug({ label: 'updateDraftMiddleware', msg: 'rejects ', err })
      return
    }
  }

  const draft = getDraftQuerySelector(gameId)(listenerAPI.getState()).data

  if (!creatorBoostCode.isValid) {
    logger.debug({ label: 'updateDraftMiddleware', msg: 'creatorboost not valid' })
    return
  }

  const market = configState.market
  if (!market) {
    logger.debug('missing market in update draft')
    return
  }

  if (
    draft &&
    action.type === 'checkout/setCreatorBoostCode' &&
    normalizeCreatorBoostCode(draft.creatorBoost) === normalizeCreatorBoostCode(creatorBoostCode.value)
  ) {
    return
  }

  const language = router.locale as SupportedLanguage

  if (reCreateCart.match(action)) {
    logger.debug('force cart re-creation')
    const parameters = {
      rows: checkoutState.rows
        .filter((r) => r.product.game === gameId)
        .map(({ product: { id: sku }, quantity }) => ({ sku, quantity })),
      countryCode: market.countryCode,
      language,
      creatorBoostCode: creatorBoostCode.value,
      paymentProvider: checkoutState.paymentProvider ?? undefined,
      gameId,
    }
    try {
      throwIfAborted(listenerAPI.signal)

      const updateResponse = await listenerAPI
        .dispatch(api.endpoints.updateDraft.initiate(parameters, { fixedCacheKey: 'updateDraft' }))
        .unwrap()
      logger.debug({ label: 'updateDraftMiddleware', msg: 'sync' })
      throwIfAborted(listenerAPI.signal)
      listenerAPI.dispatch(syncWithDraft({ draft: updateResponse, market }))
    } catch (err) {
      logger.debug({ label: 'updateDraftMiddleware', msg: 'canceled', err })
    }
  } else {
    const parameters = {
      rows: compareCartRows(draft?.rows ?? [], checkoutState.rows)
        ? checkoutState.rows
            .filter((r) => r.product.game === gameId)
            .map(({ product: { id: sku }, quantity }) => ({ sku, quantity }))
        : undefined,
      creatorBoostCode:
        normalizeCreatorBoostCode(draft?.creatorBoost) !== normalizeCreatorBoostCode(creatorBoostCode.value)
          ? creatorBoostCode.value
          : undefined,
      countryCode: market.countryCode,
      language,
      paymentProvider: checkoutState.paymentProvider ?? undefined,
      gameId,
    }
    logger.debug({ label: 'updateDraftMiddleware', msg: 'parameters', parameters })
    try {
      const updateResponse = await listenerAPI
        .dispatch(api.endpoints.updateDraft.initiate(parameters, { fixedCacheKey: 'updateDraft' }))
        .unwrap()

      const productAdded = addProduct.match(action) ? action.payload.id : undefined
      const addedProductNotEligible =
        productAdded &&
        updateResponse.rows.find((row) => row.product.id === productAdded)?.eligibility.isEligible === false

      if (addedProductNotEligible) {
        listenerAPI.dispatch(
          addNotification({
            type: 'ADD_PRODUCT',
            productId: productAdded,
            skipOpen: false,
          }),
        )
      }

      logger.debug({ label: 'updateDraftMiddleware', msg: 'sync' })
      throwIfAborted(listenerAPI.signal)
      listenerAPI.dispatch(syncWithDraft({ draft: updateResponse, market }))
    } catch (err) {
      logger.debug({ label: 'updateDraftMiddleware', msg: 'canceled', err })
    }
  }
}

const updateDraftListenerMiddleware = {
  effect: updateDraft,
  predicate: (action: AnyAction) => {
    if (setCreatorBoostCode.match(action)) {
      return action.payload.isValid && !action.payload.validating
    }

    return isAnyOf(
      reCreateCart.match,
      addProduct.match,
      removeProduct.match,
      setBillingCountry.match,
      changeLanguage.match,
    )(action)
  },
} as const

export default updateDraftListenerMiddleware
